import React, { useState } from "react";
import { trackEvent } from "../analytics";
import { getUtmParams } from "../utils/utm";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [botField, setBotField] = useState("");
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  function encode(data) {
    return Object.keys(data)
      .map(
        (key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key])
      )
      .join("&");
  }

  function validateForm() {
    let newErrors = {};
    if (!name.trim()) newErrors.name = "Name is required.";
    if (!email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Invalid email format.";
    }
    if (!message.trim()) newErrors.message = "Message cannot be empty.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (botField) {
      // Honeypot field got filled in - almost certainly a bot. Pretend
      // success without actually submitting, so it doesn't retry.
      setShowSuccess(true);
      return;
    }
    if (!validateForm()) return;

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({
        "form-name": "contact",
        name,
        email,
        phone,
        message,
        "bot-field": botField,
        ...getUtmParams(),
      }),
    })
      .then(() => {
        trackEvent("generate_lead", { form: "contact" });
        setShowSuccess(true);
        setName("");
        setEmail("");
        setPhone("");
        setMessage("");
        setErrors({});
      })
      .catch(() => alert("Error sending message ❌"));
  }

  return (
    <section id="contact" className="bg-white text-gray-900 relative">
      <div className="container px-5 pt-16 pb-10 mx-auto max-w-6xl text-center">
        <p className="text-purple-600 text-sm font-semibold tracking-widest title-font mb-3">
          LET'S CONNECT
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold title-font text-gray-900 mb-4">
          Ready to build something that works?
        </h1>
        <p className="lg:w-1/2 mx-auto leading-relaxed text-gray-600">
          Tell us what you're trying to solve. We'll come back with an honest
          read on scope, approach, and the fastest path to a working product.
        </p>
      </div>

      <div className="container px-5 pb-16 mx-auto max-w-6xl flex flex-col md:flex-row sm:flex-nowrap flex-wrap">
        <div className="lg:w-2/3 md:w-full bg-white rounded-xl overflow-hidden border border-gray-200 sm:mr-10 p-10 flex items-end justify-start relative mb-8 md:mb-0">
          <iframe
            width="100%"
            height="100%"
            title="map"
            className="absolute inset-0"
            frameBorder={0}
            marginHeight={0}
            marginWidth={0}
            src="https://www.google.com/maps/embed/v1/place?q=Cambridge,+UK&key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8"
          />
          <div className="bg-white relative flex flex-wrap py-6 rounded-xl shadow-md border border-gray-200">
            <div className="lg:w-1/2 px-6">
              <h2 className="title-font font-semibold text-gray-900 tracking-widest text-xs">
                ADDRESS
              </h2>
              <p className="mt-1 text-gray-600">
                Cambourne Business Park <br />
                Cambridge, UK CB23 6DW
              </p>
            </div>
            <div className="lg:w-1/2 px-6 mt-4 lg:mt-0">
              <h2 className="title-font font-semibold text-gray-900 tracking-widest text-xs">
                EMAIL
              </h2>
              <a
                className="text-purple-600 leading-relaxed"
                href="mailto:hello@mantletechsolutions.com"
              >
                hello@mantletechsolutions.com
              </a>
              <h2 className="title-font font-semibold text-gray-900 tracking-widest text-xs mt-4">
                PHONE
              </h2>
              <p className="leading-relaxed text-gray-600">+44(0)7741368555</p>
            </div>
          </div>
        </div>

        <form
          name="contact"
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
          className="lg:w-1/3 md:w-full flex flex-col md:ml-auto w-full md:py-8 mt-8 md:mt-0"
        >
          <h2 className="text-gray-900 sm:text-2xl text-xl mb-4 font-medium title-font">
            Book Your Clarity Call
          </h2>

          <p className="hidden" aria-hidden="true">
            <label>
              Leave this field blank
              <input
                name="bot-field"
                tabIndex="-1"
                autoComplete="off"
                value={botField}
                onChange={(e) => setBotField(e.target.value)}
              />
            </label>
          </p>

          <div className="relative mb-4">
            <label htmlFor="name" className="leading-7 text-sm text-gray-600">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white rounded-lg border border-gray-300 focus:border-purple-500 text-gray-900 py-2 px-4"
              required
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          <div className="relative mb-4">
            <label htmlFor="email" className="leading-7 text-sm text-gray-600">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white rounded-lg border border-gray-300 focus:border-purple-500 text-gray-900 py-2 px-4"
              required
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <div className="relative mb-4">
            <label htmlFor="phone" className="leading-7 text-sm text-gray-600">
              Phone Number (Optional)
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full bg-white rounded-lg border border-gray-300 focus:border-purple-500 text-gray-900 py-2 px-4"
            />
          </div>

          <div className="relative mb-4">
            <label
              htmlFor="message"
              className="leading-7 text-sm text-gray-600"
            >
              Message
            </label>
            <textarea
              id="message"
              name="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full bg-white rounded-lg border border-gray-300 focus:border-purple-500 text-gray-900 py-2 px-4 h-32 resize-none"
              required
            />
            {errors.message && (
              <p className="text-red-500 text-xs mt-1">{errors.message}</p>
            )}
          </div>

          <button
            type="submit"
            className="text-white bg-purple-600 border-0 py-2 px-6 focus:outline-none hover:bg-purple-700 rounded-full text-lg"
          >
            Submit
          </button>
        </form>
      </div>

      {showSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <div className="bg-white text-gray-900 p-6 rounded-xl shadow-lg w-96 text-center border border-gray-200">
            <h3 className="text-xl font-semibold">
              Message Sent Successfully! ✅
            </h3>
            <p className="text-gray-600 mt-2">We'll get back to you shortly.</p>
            <button
              onClick={() => setShowSuccess(false)}
              className="mt-4 px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-full text-white"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
